/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Wrapper that composes the SSR mount guard, HMR error boundary, and Suspense
 * fallback needed by every Three.js scene on the site. Drop in a lazy-loaded
 * scene component as children and the shell handles the rest.
 */

import { Suspense, useEffect, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useThreeScene } from '@/hooks/useThreeScene';
import { CanvasErrorBoundary } from '@/components/CanvasErrorBoundary';
import { SceneLoadingFallback } from '@/components/SceneLoadingFallback';
import { analytics } from '@/lib/analytics';

interface ThreeSceneShellProps {
  children: ReactNode;
  /** MUI sx overrides for the outer container */
  sx?: SxProps<Theme>;
  /** Optional message shown beneath the loading animation */
  message?: string;
  /** Content to render when WebGL is not available */
  fallback?: ReactNode;
  /**
   * Parent already waited for a client mount (e.g. home Finder).
   * Skip the Lottie placeholder so the canvas can paint in this commit.
   */
  clientReady?: boolean;
}

export function ThreeSceneShell({ children, sx, message, fallback, clientReady }: ThreeSceneShellProps) {
  const { isMounted, hasWebGL } = useThreeScene();
  const ready = clientReady || isMounted;
  const loading = message ? <SceneLoadingFallback message={message} /> : null;

  useEffect(() => {
    if (!ready) return;
    analytics.sceneLoaded(hasWebGL);
  }, [ready, hasWebGL]);

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        ...sx,
      }}
    >
      {ready && !hasWebGL && fallback ? (
        fallback
      ) : ready ? (
        <CanvasErrorBoundary>
          <Suspense fallback={loading}>{children}</Suspense>
        </CanvasErrorBoundary>
      ) : (
        loading
      )}
    </Box>
  );
}
